import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { TrainingPageView } from "../VIEW/TrainingPageView";
import { TrainingData, WellnessDay } from "../types/training";
import { useAuth } from "../FIREBASE/useAuth";
import {
  fetchTrainingData,
  deleteTrainingEvent,
  updateTrainingEvent,
  updateEventDescription,
  postActivityMessage,
  createTrainingEvent,
  CreateEventPayload,
  setIntervalsCreds,
  hasIntervalsCreds,
} from "../FIREBASE/intervals";
import { loadCredentials } from "../FIREBASE/userSettings";
import { initCloudSync } from "../COMPONENTS/cloudSync";
import { loadProfile, saveProfile } from "../COMPONENTS/plannerProfile";
import { TrainingStatusScreen } from "../COMPONENTS/TrainingStatusScreen";
import routeUrls from "../COMPONENTS/navbar/navLink";

function TrainingPage() {
  const { user, loading: authLoading } = useAuth();
  const history = useHistory();
  const [data, setData] = useState<TrainingData | null>(null);
  const [error, setError] = useState("");

  // Redirect unauthenticated visitors before any data is fetched
  useEffect(() => {
    if (!authLoading && !user) {
      history.replace(`${routeUrls.LOGIN}?from=${routeUrls.TRAINING}`);
    }
  }, [user, authLoading]);

  // Load credentials (if needed) then fetch training data in one async flow,
  // eliminating the render cycle between credential load and data fetch.
  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;

    async function init() {
      if (!hasIntervalsCreds()) {
        const creds = await loadCredentials(user!.uid);
        if (cancelled) return;
        if (!creds) { history.push(routeUrls.CONNECT); return; }
        setIntervalsCreds(creds.apiKey, creds.athleteId);
      }

      // Pull account-synced data (incl. plan-week targets) into localStorage so
      // the dashboard shows it even on a fresh device that opens /training first.
      await initCloudSync(user!.uid);
      if (cancelled) return;

      try {
        const { wellness: wellnessArr, activities, events } = await fetchTrainingData();
        if (cancelled) return;
        const wellnessHistory: WellnessDay[] = (wellnessArr as any[]).map((w: any) => ({
          id: w.id, ctl: w.ctl ?? null, atl: w.atl ?? null,
          hrv: w.hrv ?? null, restingHR: w.restingHR ?? null,
        }));
        const latest = wellnessHistory[wellnessHistory.length - 1];
        setData({
          wellness: { ctl: latest?.ctl ?? null, atl: latest?.atl ?? null, restingHR: latest?.restingHR ?? null, hrv: latest?.hrv ?? null },
          wellnessHistory,
          activities: activities as any[],
          events: events as any[],
        });
      } catch (err) {
        if (!cancelled) { console.error("Training data error:", err); setError("Could not load training data."); }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [user, authLoading, history]);

  const deleteEvent = useCallback(async (id: number) => {
    await deleteTrainingEvent(id);
    setData((prev) =>
      prev ? { ...prev, events: prev.events.filter((e) => e.id !== id) } : null
    );
  }, []);

  const updateEvent = useCallback(
    (id: number, startDateLocal: string) => updateTrainingEvent(id, startDateLocal),
    []
  );

  const [sickSince, setSickSince] = useState<string | null>(() => loadProfile()?.sickSince ?? null);

  // Mark a day sick: record sickSince (the earliest sick day) so the plan ramps
  // back on recovery. Does NOT delete the day's workouts — they stay on the
  // calendar flagged red (suggested rest) so the athlete decides whether to skip
  // or remove them.
  const markSick = useCallback(async (dateStr: string): Promise<void> => {
    const prof = loadProfile();
    const since = prof?.sickSince && prof.sickSince < dateStr ? prof.sickSince : dateStr;
    if (prof) saveProfile({ ...prof, sickSince: since });
    setSickSince(since);
  }, []);

  // Recovered today — record the illness as a closed range (so the plan applies
  // a gradual return-to-training ramp) and clear the sick flag.
  const endSick = useCallback(async () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const prof = loadProfile();
    if (prof && prof.sickSince) {
      saveProfile({
        ...prof,
        sickSince: null,
        blockedRanges: [...(prof.blockedRanges ?? []), { id: Math.random().toString(36).slice(2, 9), start: prof.sickSince, end: todayStr, label: "Sjuk" }],
      });
    }
    setSickSince(null);
  }, []);

  const createEvent = useCallback(async (payload: CreateEventPayload) => {
    const id = await createTrainingEvent(payload);
    setData((prev) => prev ? {
      ...prev,
      events: [...prev.events, {
        id, name: payload.name, description: payload.description,
        start_date_local: payload.start_date_local, category: payload.category, type: payload.type,
      } as any],
    } : prev);
  }, []);

  const editEventDescription = useCallback(async (id: number, description: string) => {
    await updateEventDescription(id, description);
    setData((prev) =>
      prev ? { ...prev, events: prev.events.map((e) => e.id === id ? { ...e, description } : e) } : null
    );
  }, []);

  const handlePostActivityMessage = useCallback(
    (activityId: string, content: string) =>
      postActivityMessage(activityId, content),
    []
  );

  if (authLoading || !user) return null;
  if (error)
    return (
      <TrainingStatusScreen
        title="Träning"
        backTo={routeUrls.HOME}
        backLabel="← Hem"
        message="Kunde inte ladda träningsdata."
        tone="error"
        onRetry={() => window.location.reload()}
      />
    );
  return (
    <TrainingPageView
      data={data}
      deleteEvent={deleteEvent}
      updateEvent={updateEvent}
      editEventDescription={editEventDescription}
      sickSince={sickSince}
      onMarkSick={markSick}
      onEndSick={endSick}
      postActivityMessage={handlePostActivityMessage}
      onCreateEvent={createEvent}
    />
  );
}

export default TrainingPage;
